import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ComputerUseApi from "../apis/computerUseApi";
import ComputerUseRunPanel from "../components/ComputerUseRun";
import { ActionTypes } from "../store/actionTypes";
import { placeRuns } from "../utils/computerUseRunPlacement";

/**
 * A thread's browser runs, ready to draw beside the messages they happened between.
 *
 * Lives in a hook because every chat surface needs the same three things and none of them
 * are surface-specific: the stored runs seeded on load, the live ones folded in from the
 * stream, and each placed at the message it followed. Held in one place so a run cannot be
 * watchable in the assistant and invisible on a ticket -- which is what happened while the
 * wiring lived in ChatAssistant alone, and left a technician locked out of a browser the
 * composer told them to go and watch.
 *
 * `messages` must be the same list, in the same order, that the caller renders -- placement
 * is by position, so a different array silently puts panels in the wrong gaps.
 */
const useComputerUseRuns = (
  aiThreadId: number | string | null | undefined,
  messages: any[]
) => {
  const dispatch = useDispatch();
  const threadKey = aiThreadId != null ? String(aiThreadId) : null;

  const runs = useSelector((state: any) =>
    threadKey ? state.chat?.computerUseByThreadId?.[threadKey]?.runs ?? null : null
  );

  /**
   * Seed the thread's browser runs from the stored rows.
   *
   * The live stream carries all of this while a run is happening; this is for the client that
   * was not watching -- a reload, a second tab, a thread opened next week. Anything the stream
   * has already put in place wins, so an answer that arrives late cannot roll a run backwards.
   */
  useEffect(() => {
    if (!aiThreadId) return undefined;
    let cancelled = false;

    new ComputerUseApi()
      .listRuns(aiThreadId)
      .then(({ runs: stored }) => {
        if (cancelled || !stored?.length) return;
        dispatch({
          type: ActionTypes.SET_COMPUTER_USE_RUNS,
          payload: { threadId: String(aiThreadId), runs: stored },
        });
      })
      .catch(() => {
        // A thread whose browser history cannot be read is still a usable thread. The panels
        // are the only thing lost, and an error banner over the transcript would cost more.
      });

    return () => {
      cancelled = true;
    };
  }, [aiThreadId, dispatch]);

  const placement = useMemo(
    () => placeRuns(messages || [], Object.values(runs || {})),
    [messages, runs]
  );

  return React.useCallback(
    (index: number) => {
      const here = placement.get(index);
      if (!here?.length || !aiThreadId) return null;
      // One panel per browser, not per tool call: a run's sessions happened at different points
      // in the conversation, and each is drawn at its own.
      return here.map((session) => (
        <ComputerUseRunPanel
          key={`cu-${session.key}`}
          run={session.run}
          recording={session.recording}
          aiThreadId={aiThreadId}
        />
      ));
    },
    [placement, aiThreadId]
  );
};

export default useComputerUseRuns;
